import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Swal from 'sweetalert2';
import {
  FiCopy,
  FiEdit2,
  FiExternalLink,
  FiFileText,
  FiMapPin,
  FiSmartphone,
  FiTrash2,
  FiUserPlus,
  FiUsers,
} from 'react-icons/fi';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { AcaoIcone } from '../../components/AcaoIcone';
import { Input } from '../../components/Input';
import { MoedaInput } from '../../components/MoedaInput';
import { PasswordInput } from '../../components/PasswordInput';
import { Select } from '../../components/Select';
import { Table } from '../../components/Table';
import {
  atualizarFuncionario,
  criarFuncionario,
  DadosFuncionario,
  listarFuncionarios,
  removerFuncionario,
} from '../../api/funcionarios';
import { Funcionario } from '../../types';
import { formatarCelular } from '../../utils/mascaras';
import { formatarMoeda } from '../../utils/formatadores';

const FORM_INICIAL: DadosFuncionario = {
  nome: '',
  cargo: '',
  celular: '',
  cnh: '',
  salario: '',
  tipo_contrato: 'clt',
  data_admissao: '',
  eh_motoboy: false,
  app_motoboy_ativo: false,
  senha_app: '',
};

const OPCOES_CONTRATO = [
  { value: 'clt', label: 'CLT' },
  { value: 'outro', label: 'Outro' },
];

function badgeMotoboy(ehMotoboy?: boolean) {
  return ehMotoboy
    ? { texto: 'Motoboy', fundo: 'rgba(30, 158, 88, 0.12)', cor: 'var(--cor-sucesso)' }
    : { texto: 'Equipe', fundo: 'rgba(30, 86, 232, 0.1)', cor: 'var(--cor-primaria)' };
}

export function EquipeLista() {
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [form, setForm] = useState<DadosFuncionario>(FORM_INICIAL);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [salvando, setSalvando] = useState(false);

  function carregar() {
    setCarregando(true);
    listarFuncionarios()
      .then(setFuncionarios)
      .finally(() => setCarregando(false));
  }

  useEffect(carregar, []);

  function handleChange(campo: keyof DadosFuncionario) {
    return (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((atual) => ({ ...atual, [campo]: event.target.value }));
  }

  function iniciarEdicao(funcionario: Funcionario) {
    setEditandoId(funcionario.id);
    setForm({
      nome: funcionario.nome,
      cargo: funcionario.cargo || '',
      celular: funcionario.celular || '',
      cnh: funcionario.cnh || '',
      salario: funcionario.salario,
      tipo_contrato: funcionario.tipo_contrato,
      data_admissao: funcionario.data_admissao || '',
      eh_motoboy: Boolean(funcionario.eh_motoboy),
      app_motoboy_ativo: Boolean(funcionario.app_motoboy_ativo),
      senha_app: '',
    });
  }

  function cancelarEdicao() {
    setEditandoId(null);
    setForm(FORM_INICIAL);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSalvando(true);

    try {
      if (editandoId) {
        await atualizarFuncionario(editandoId, form);
      } else {
        await criarFuncionario(form);
      }
      cancelarEdicao();
      carregar();
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Nao foi possivel salvar',
        text: error.response?.data?.erro || 'Verifique os dados informados.',
      });
    } finally {
      setSalvando(false);
    }
  }

  async function handleRemover(funcionario: Funcionario) {
    const confirmacao = await Swal.fire({
      icon: 'warning',
      title: `Desligar "${funcionario.nome}"?`,
      showCancelButton: true,
      confirmButtonText: 'Desligar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmacao.isConfirmed) {
      return;
    }

    await removerFuncionario(funcionario.id);
    carregar();
  }

  function verEncargos(funcionario: Funcionario) {
    const encargos = funcionario.encargos;

    if (!encargos) {
      Swal.fire({
        title: funcionario.nome,
        html: `Contrato "outro" sem encargos CLT.<br>Custo total: <strong>${formatarMoeda(funcionario.custo_total)}</strong>`,
      });
      return;
    }

    Swal.fire({
      title: `Encargos CLT - ${funcionario.nome}`,
      html: `
        <div style="text-align:left; font-size:14px;">
          Salario base: ${formatarMoeda(funcionario.salario)}<br>
          INSS patronal (20%): ${formatarMoeda(encargos.inss_patronal)}<br>
          FGTS (8%): ${formatarMoeda(encargos.fgts)}<br>
          Provisao 13 salario: ${formatarMoeda(encargos.provisao_decimo_terceiro)}<br>
          Provisao ferias + 1/3: ${formatarMoeda(encargos.provisao_ferias)}<br>
          <hr>
          Total de encargos: <strong>${formatarMoeda(encargos.total_encargos)}</strong><br>
          Custo total mensal: <strong>${formatarMoeda(encargos.custo_total)}</strong>
        </div>
      `,
    });
  }

  async function copiarLink(link?: string | null) {
    if (!link) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        window.prompt('Copie o link:', link);
      }
      Swal.fire({ icon: 'success', title: 'Link copiado!', timer: 1200, showConfirmButton: false });
    } catch {
      window.prompt('Copie o link:', link);
    }
  }

  function abrirLink(link?: string | null) {
    if (!link) return;
    window.open(link, '_blank', 'noopener,noreferrer');
  }

  const motoboyAtivo = Boolean(form.eh_motoboy);

  return (
    <AppLayout>
      <div className="lizzi-cabecalho-pagina">
        <h1>
          <span className="lizzi-icone-titulo">
            <FiUsers size={18} />
          </span>
          Equipe
        </h1>
      </div>

      <Card style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '15px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiUserPlus size={16} style={{ color: 'var(--cor-primaria)' }} />
          {editandoId ? 'Editar colaborador' : 'Novo colaborador'}
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="lizzi-equipe-grid">
            <Input label="Nome" value={form.nome} onChange={handleChange('nome')} required />
            <Input label="Cargo" value={form.cargo} onChange={handleChange('cargo')} />
            <Input
              label="Celular"
              value={form.celular}
              onChange={(event) => setForm((atual) => ({ ...atual, celular: formatarCelular(event.target.value) }))}
              maxLength={15}
            />
            <MoedaInput
              label="Salario"
              value={form.salario}
              onValueChange={(valor) => setForm((atual) => ({ ...atual, salario: valor }))}
              required
            />
            <Select label="Contrato" value={form.tipo_contrato} onChange={handleChange('tipo_contrato')} options={OPCOES_CONTRATO} />
            <Input label="Admissao" type="date" value={form.data_admissao} onChange={handleChange('data_admissao')} />
            {motoboyAtivo ? <Input label="CNH" value={form.cnh} onChange={handleChange('cnh')} required /> : null}
            {motoboyAtivo && form.app_motoboy_ativo ? (
              <PasswordInput
                label={editandoId ? 'Nova senha do app' : 'Senha do app'}
                value={form.senha_app}
                onChange={handleChange('senha_app')}
                placeholder={editandoId ? 'Preencha so se quiser trocar' : 'Defina uma senha'}
                required={!editandoId}
              />
            ) : null}
          </div>

          <div className="lizzi-equipe-actions">
            <div style={{ display: 'grid', gap: '10px' }}>
              <Form.Check
                id="eh-motoboy"
                type="switch"
                label="Esse colaborador faz entregas como motoboy"
                checked={Boolean(form.eh_motoboy)}
                onChange={(event) =>
                  setForm((atual) => ({
                    ...atual,
                    eh_motoboy: event.target.checked,
                    app_motoboy_ativo: event.target.checked ? atual.app_motoboy_ativo : false,
                    cnh: event.target.checked ? atual.cnh : '',
                    senha_app: '',
                  }))
                }
              />

              {motoboyAtivo ? (
                <Form.Check
                  id="app-motoboy"
                  type="switch"
                  label="Liberar login no app do motoboy"
                  checked={Boolean(form.app_motoboy_ativo)}
                  onChange={(event) =>
                    setForm((atual) => ({
                      ...atual,
                      app_motoboy_ativo: event.target.checked,
                      senha_app: '',
                    }))
                  }
                />
              ) : null}
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Button type="submit" disabled={salvando} fullWidth={false}>
                {editandoId ? 'Salvar' : 'Adicionar'}
              </Button>
              {editandoId ? (
                <Button type="button" variant="outline" onClick={cancelarEdicao} fullWidth={false}>
                  Cancelar
                </Button>
              ) : null}
            </div>
          </div>
        </form>
      </Card>

      {carregando ? (
        <p>Carregando...</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Contato</th>
              <th>Tipo</th>
              <th>App</th>
              <th>Contrato</th>
              <th>Salario</th>
              <th>Custo total</th>
              <th>Links</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {funcionarios.map((funcionario) => {
              const badge = badgeMotoboy(funcionario.eh_motoboy);

              return (
                <tr key={funcionario.id}>
                  <td>
                    <strong>{funcionario.nome}</strong>
                    <div style={{ color: '#5c6b84', fontSize: '12px', marginTop: 4 }}>
                      {funcionario.cargo || 'Sem cargo'}
                      {funcionario.cnh ? ` | CNH ${funcionario.cnh}` : ''}
                    </div>
                  </td>
                  <td>{funcionario.celular || '-'}</td>
                  <td>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        borderRadius: '999px',
                        padding: '5px 10px',
                        background: badge.fundo,
                        color: badge.cor,
                        fontSize: '12px',
                        fontWeight: 700,
                      }}
                    >
                      {funcionario.eh_motoboy ? <FiMapPin size={13} /> : null}
                      {badge.texto}
                    </span>
                  </td>
                  <td>
                    {funcionario.eh_motoboy ? (
                      <span className={`lizzi-status-pill ${funcionario.app_motoboy_ativo ? 'sucesso' : 'neutro'}`}>
                        {funcionario.app_motoboy_ativo ? 'Liberado' : 'Desligado'}
                      </span>
                    ) : (
                      <span style={{ opacity: 0.55 }}>-</span>
                    )}
                  </td>
                  <td>{funcionario.tipo_contrato === 'clt' ? 'CLT' : 'Outro'}</td>
                  <td>{formatarMoeda(funcionario.salario)}</td>
                  <td>{formatarMoeda(funcionario.custo_total)}</td>
                  <td>
                    {funcionario.eh_motoboy ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {funcionario.link_login_app ? (
                          <>
                            <AcaoIcone
                              icon={<FiCopy size={15} />}
                              onClick={() => copiarLink(funcionario.link_login_app)}
                              aria-label="Copiar acesso do app"
                            />
                            <AcaoIcone
                              icon={<FiSmartphone size={15} />}
                              onClick={() => abrirLink(funcionario.link_login_app)}
                              aria-label="Abrir login do app"
                            />
                          </>
                        ) : null}
                        {funcionario.link_rastreamento ? (
                          <AcaoIcone
                            icon={<FiExternalLink size={15} />}
                            onClick={() => abrirLink(funcionario.link_rastreamento)}
                            aria-label="Abrir rastreamento"
                          />
                        ) : null}
                      </div>
                    ) : (
                      <span style={{ opacity: 0.55 }}>-</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <AcaoIcone icon={<FiFileText size={15} />} onClick={() => verEncargos(funcionario)} aria-label="Encargos" />
                      <AcaoIcone icon={<FiEdit2 size={15} />} onClick={() => iniciarEdicao(funcionario)} aria-label="Editar" />
                      <AcaoIcone icon={<FiTrash2 size={15} />} tone="perigo" onClick={() => handleRemover(funcionario)} aria-label="Desligar" />
                    </div>
                  </td>
                </tr>
              );
            })}

            {funcionarios.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: '16px', textAlign: 'center' }}>
                  Nenhum colaborador cadastrado ainda.
                </td>
              </tr>
            ) : null}
          </tbody>
        </Table>
      )}
    </AppLayout>
  );
}
